/**
 * FeatureGate — blocks pages whose feature isn't in the user's package.
 * Fail-open on API errors; unmapped routes always render.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, Wallet, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { safeBack } from "@/lib/safeBack";
import { featureForPath, useEntitlements } from "@/lib/featureAccess";
import { tr } from "@/lib/tr";

function LockedScreen({ featureKey, ent }) {
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    useEffect(() => {
        api.get("/packages").then(({ data }) => setInfo(data)).catch(() => {});
    }, []);
    const label = info?.catalog?.find((f) => f.key === featureKey)?.label || "This feature";
    const includedIn = (info?.packages || []).filter((p) => p.features?.[featureKey]);
    return (
        <div className="min-h-screen vt-pattern flex items-center justify-center px-5" data-testid="feature-locked-screen">
            <div className="max-w-md w-full bg-white rounded-2xl border border-[#E5E5E5] shadow-[0_16px_44px_rgba(15,51,17,0.10)] p-7 text-center">
                <span className="mx-auto h-14 w-14 rounded-full bg-[#FFF3E0] flex items-center justify-center">
                    <Lock className="h-6 w-6 text-[#F57C00]" />
                </span>
                <h1 className="mt-4 text-[20px] font-bold text-[#0F3311]" data-testid="feature-locked-title">{tr(label)} {tr("is locked")}</h1>
                <p className="mt-1.5 text-[13.5px] text-[#6B7A70]">
                    {tr("Your current package")}{ent?.effective_package ? ` (${tr(ent.effective_package.name)})` : ""} {tr("doesn't include this feature.")}
                </p>
                {includedIn.length > 0 && (
                    <div className="mt-4 rounded-xl bg-[#F7F9F4] border border-[#E1E8DB] p-3.5 text-left">
                        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8B9B8D] mb-1.5">{tr("Included in")}</p>
                        <ul className="space-y-1">
                            {includedIn.map((p) => (
                                <li key={p.id} className="flex items-center gap-2 text-[13px] text-[#0F3311] font-medium">
                                    <Check className="h-3.5 w-3.5 text-[#1B5E20]" />
                                    {tr(p.name)} — ₹{Number(p.price_inr).toLocaleString("en-IN")}
                                    <span className="text-[#8A9891] font-normal">{p.period === "lifetime" ? tr("one-time") : `/${tr(p.period)}`}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="mt-5 grid grid-cols-2 gap-3">
                    <Button variant="outline" data-testid="feature-locked-back" onClick={() => safeBack(navigate, "/dashboard")} className="h-11 rounded-full border-[#1B5E20]/30 text-[#1B5E20]">
                        <ArrowLeft className="h-4 w-4 mr-1.5" /> {tr("Go Back")}
                    </Button>
                    <Button data-testid="feature-locked-upgrade" onClick={() => navigate("/plans")} className="h-11 rounded-full bg-[#F57C00] hover:bg-[#d96e00] text-white">
                        <Wallet className="h-4 w-4 mr-1.5" /> {tr("View Packages")}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export function FeatureGate({ children }) {
    const location = useLocation();
    const featureKey = featureForPath(location.pathname);
    const ent = useEntitlements();
    if (!featureKey) return children;
    if (!ent) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="feature-gate-loading">
                <div className="h-8 w-8 rounded-full border-2 border-[#1B5E20] border-t-transparent animate-spin" />
            </div>
        );
    }
    if (ent.error || !ent.features || ent.features[featureKey]) return children;
    return <LockedScreen featureKey={featureKey} ent={ent} />;
}

export default FeatureGate;
